/**
 * One job, as intake knows it (#115): what came in about it.
 *
 * A job is the rental system's record and intake never holds one
 * (ADR-0016). What intake holds is everything that was *said* about it
 * — the Inquiry that drafted it and every call whose caller picked it
 * off the spoken list — and until this page those were rows on a log,
 * opened one at a time, with nothing on any of them saying they were
 * about the same thing. The demo Company's gala day was a dozen calls
 * and one Inquiry.
 *
 * **One page, whether or not there is a job.** An arrival about no job
 * — a wrong number, a call about a booking somebody else took, a form
 * that failed to draft — is a job of one and is read here, in the same
 * order with the same parts. It had a page of its own until the two
 * were put side by side: one was a fact sheet with a transcript under
 * it and the other a rail of folded calls, which read as two products
 * to a Company that has both open in the same afternoon. What a job in
 * the rental system holds is not restated here (#119) and that is a
 * content difference, not a shape: a form's facts and answers are drawn
 * in the notes, because an enquiry waiting for Send is read here before
 * anything else holds it, and a call's are not, its Transcript being the
 * record; both sit in the notes with
 * everything else the page states about the arrival, and the way out is
 * on top where the Backend said where it put the job.
 *
 * **A thread of one is not a thread.** One arrival is drawn open, in
 * place, with no rail and no heading over it: a rail with a single mark
 * on it is a list of one, and folding the only thing on the page is a
 * press to see what the reader came for. The rail appears when there is
 * a second thing to put on it.
 *
 * The heading is the job's name and the one way out to the job in the
 * Company's own rental system; under it, what they asked for — the
 * call's summary, or the Customer's own words — and then the arrival
 * itself. Where there are several, they are in the order they happened
 * down one rail, folded to when each arrived, how, and what it said cut
 * short, and opened in place for the whole of it: the paragraph, who
 * rang, the recording and the Transcript. A dozen calls open at once
 * was a dozen transcripts to scroll past to reach the one that
 * mattered; what a reader scans for is when and what happened, so that
 * is the fold.
 *
 * What it does not do is read the job back, or restate it. The job's
 * date, venue, guest count and contact are on the opportunity already,
 * and it carried a card of them until #119 — a second copy of that
 * system for the Company to keep straight. Its lines, its price, its
 * Phase and the Change Proposals resting on it are the rental system's
 * too; the link out is the whole of this page's relationship with it.
 *
 * A call is edited wherever it is read (#89): its summary and the
 * person's email commit when they are left. It has to be, because a
 * call about a job is read here and nowhere else — its own path comes
 * here.
 *
 * The page waits for its recordings before it is drawn, up to a cap,
 * and holds them once read (`lib/recordings`), so a call opens with its
 * player there and reopens without moving the file again.
 */
import { useState, type MouseEvent, type ReactNode } from "react";
import { AdminPageHeader } from "../ui/AdminPageHeader";
import { Banner } from "../ui/Banner";
import { Button } from "../ui/Button";
import { ConfirmModal } from "../ui/ConfirmModal";
import { ChevronDownIcon, TrashIcon } from "../ui/icons";
import {
  CallEmail,
  CallSummary,
  Fact,
  Outcome,
  Recording,
  Transcript,
  usePlayback,
} from "./CallParts";
import {
  closeInquiry,
  sendInquiry,
  type Job,
  type JobEntry,
  type JobInquiry,
} from "../../lib/arrivals-api";
import {
  arrivedBadgeClass,
  arrivedLabel,
  formatWhenFull,
  whoLabel,
  whoLine,
  type OutcomeFacts,
} from "../../lib/arrivals-format";
import { useAction } from "../../lib/use-action";
import styles from "./Record.module.css";

/** One call in the thread changed on this screen. */
export type EntryEdited = (id: string, next: Partial<JobEntry>) => void;

/**
 * One call in the thread is to go (#158). The route owns what that
 * means: a follow-up goes on its own and the thread is read again
 * without it, where the call the job was drafted from is the thread
 * and takes the whole of it. Rejects with what went wrong, which the
 * confirm shows in place.
 */
export type EntryDeleted = (entry: JobEntry) => Promise<void>;

export function ArrivalRecord({
  job,
  loadError,
  backTo,
  onEdited,
  onDelete,
  onSent,
}: {
  job: Job | null;
  loadError: string | null;
  backTo: string;
  onEdited: EntryEdited;
  onDelete: EntryDeleted;
  /**
   * The open enquiry was sent or closed. The reader is put back on the
   * list, where a sent enquiry stays and says it is being drafted
   * (migration 072).
   */
  onSent: () => void;
}) {
  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {job ? (
          <Opened
            job={job}
            backTo={backTo}
            onEdited={onEdited}
            onDelete={onDelete}
            onSent={onSent}
          />
        ) : (
          <AdminPageHeader
            title="Enquiries"
            links={[{ to: backTo, label: "Back" }]}
          />
        )}

        {loadError && (
          <Banner variant="error" className={styles.loadBanner}>
            {loadError}
          </Banner>
        )}

        {!job && !loadError && <p className={styles.loading}>Loading…</p>}
      </div>
    </div>
  );
}

/** The job, or the one arrival, as it is read. */
function Opened({
  job,
  backTo,
  onEdited,
  onDelete,
  onSent,
}: {
  job: Job;
  backTo: string;
  onEdited: EntryEdited;
  onDelete: EntryDeleted;
  onSent: () => void;
}) {
  const who = whoLabel(job.who, job.inquiry?.contact_phone ?? "");
  // A thread of one is not a thread: it is drawn open, in place.
  const alone = job.thread.length === 1 ? job.thread[0] : null;
  const calls = job.thread.filter((e) => e.kind === "call").length;
  // The job is named by its event, off the Inquiry; a job intake never
  // drafted, or one whose Inquiry gave no name, is headed by the
  // Customer.
  const title = job.title || who;
  const subtitle =
    [
      job.quote_number && `Quote #${job.quote_number}`,
      title !== who && who,
      // Where they rang from, and when: on a rail each entry says its
      // own time, and alone there is no fold to say it.
      alone && alone.who_org !== who && alone.who_org,
      alone ? formatWhenFull(alone.at) : calls >= 2 && `${calls} calls`,
    ]
      .filter(Boolean)
      .join(" · ") || undefined;
  // The one way out: the job itself, where the Company's rental system
  // holds it. A job drafted before the Backend said where it put it has
  // no link to offer.
  const openIn = job.workspace_url
    ? [
        {
          href: job.workspace_url,
          label: `Open in ${job.rental_system || "your rental system"}`,
        },
      ]
    : [];

  return (
    <>
      <AdminPageHeader
        title={title}
        subtitle={subtitle}
        links={[...openIn, { to: backTo, label: "Back" }]}
      />

      {alone ? (
        <OneArrival
          entry={alone}
          job={job}
          onEdited={(next) => onEdited(alone.id, next)}
          onDelete={onDelete}
          onSent={onSent}
        />
      ) : (
        <>
          {/* What they asked for: the Inquiry's one paragraph, the same
              one the job's description in the rental system opens with,
              whole and first, with who to ring back and what to do
              under it. A second call about the job changes what the
              page lists and nothing about how one arrival reads, so the
              notes are the notes whether there is one of them or five. */}
          {job.inquiry && (
            <Notes
              rows={
                <>
                  {job.inquiry.contact_phone && (
                    <Fact label="Phone number">
                      {job.inquiry.contact_phone}
                    </Fact>
                  )}
                  {job.inquiry.contact_email && (
                    <Fact label="Email">{job.inquiry.contact_email}</Fact>
                  )}
                  <FormRows record={job.inquiry} />
                  <Outcome facts={jobOutcomeFacts(job, job.inquiry)} />
                </>
              }
            >
              {job.inquiry.asked_for && (
                <p className={styles.askedFor}>{job.inquiry.asked_for}</p>
              )}
            </Notes>
          )}
          <OpenEnquiryActions job={job} onSent={onSent} />
          {job.thread.length > 0 && (
            <>
              <h2 className={styles.sectionHeading}>Enquiry thread</h2>
              {/* The rail: one hairline down the left with a mark per
                  entry on it, so the thread reads as a line of events
                  before a word of it is read. */}
              <ol className={styles.rail}>
                {job.thread.map((entry, i) => (
                  <ThreadEntry
                    key={entry.id}
                    entry={entry}
                    job={job}
                    last={i === job.thread.length - 1}
                    onEdited={(next) => onEdited(entry.id, next)}
                    onDelete={onDelete}
                  />
                ))}
              </ol>
            </>
          )}
        </>
      )}
    </>
  );
}

/**
 * The notes, in the box: what they asked for and who to ring back.
 *
 * The same box on both pages, because it is the same thing being read —
 * the Inquiry's one paragraph the job's description opens with, or,
 * where nothing drafted, what was said on the call, corrected in place.
 *
 * The number and the email are in it rather than in a fact sheet under
 * it. They were the first three rows of a sheet whose other rows are
 * the job's and are not drawn where the rental system holds them
 * (#119), which left a bordered strip holding the two facts the notes
 * are acted on with: a person reads what they wanted and rings them
 * back, and those are one thing to read, not two.
 *
 * Everything else the page states about the arrival is in it for the
 * same reason, the Outcome last: what happened and what to do about it,
 * on every arrival, as one sentence rather than a second list under the
 * notes.
 */
function Notes({
  arrived,
  rows,
  children,
}: {
  arrived?: string;
  rows?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className={styles.notes}>
      <div className={styles.notesHead}>
        <span className={styles.notesLabel}>Enquiry notes</span>
        {/* How it reached the Company is the one thing a phone lead
            says that a form lead does not (CONTEXT.md's Provenance), so
            it stays on the notes it is about. */}
        {arrived && (
          <span className={arrivedBadgeClass(arrived)}>
            {arrivedLabel(arrived)}
          </span>
        )}
      </div>
      {children}
      {rows && <dl className={styles.notesRows}>{rows}</dl>}
    </div>
  );
}

/**
 * The one thing that came in, drawn open.
 *
 * The order is what a person asked for it: what was said, in a sentence
 * or two and to be corrected in place; the few facts the arrival itself
 * carries — how it reached the Company, the number to ring back and the
 * person's email; and then the whole of what was said.
 *
 * All of it is in the notes. A row drawn under the box read as a second
 * thing to take in, and what a person does with an arrival is read what
 * they wanted, see what became of it and ring them back — one box, not
 * a box and a strip beneath it. Only the Transcript is outside it,
 * which is the call itself and not a fact about the call.
 *
 * A call's Inquiry draws no fact sheet and no answers: the Transcript
 * is the record (#119). A form submission draws everything the form
 * collected, under the number and email: an enquiry waits for Send
 * before it reaches the rental system, so this is where it is read
 * first (FormRows). The Outcome
 * is on every arrival: what happened, and what to do about it, with how
 * they asked to be got back to as the second half of that sentence
 * rather than a row of its own.
 */
function OneArrival({
  entry,
  job,
  onEdited,
  onDelete,
  onSent,
}: {
  entry: JobEntry;
  job: Job;
  onEdited: (next: Partial<JobEntry>) => void;
  onDelete: EntryDeleted;
  onSent: () => void;
}) {
  const isCall = entry.kind === "call";
  const record = job.inquiry;
  // The Customer's own words, where nobody was on the phone to summarise.
  const said = record?.asked_for || entry.said;
  // Who to ring back, in the notes. The record's own copies of them are
  // not drawn again in the sheet under it.
  const contact = (
    <>
      {entry.from_e164 && entry.who && (
        <Fact label="Phone number">{entry.from_e164}</Fact>
      )}
      {isCall ? (
        <CallEmail id={entry.id} email={entry.email} onEdited={onEdited} />
      ) : (
        entry.email && <Fact label="Email">{entry.email}</Fact>
      )}
    </>
  );
  return (
    <>
      <Notes
        arrived={entry.arrived}
        rows={
          <>
            {contact}
            {!isCall && record && <FormRows record={record} />}
            <Outcome facts={outcomeFacts(job, entry)} />
          </>
        }
      >
        {isCall ? (
          <CallSummary
            id={entry.id}
            summary={entry.summary}
            onEdited={onEdited}
            className=""
          />
        ) : (
          <p className={styles.askedFor}>{said}</p>
        )}
      </Notes>

      <OpenEnquiryActions job={job} onSent={onSent} />

      {isCall && <CallBody entry={entry} onDelete={onDelete} where="page" />}
    </>
  );
}

/**
 * Everything a form collected, as rows of the notes: the event's facts,
 * then the Profile's own questions as the Company wrote them with what
 * was answered. The server sends them for a form alone and leaves out
 * what was not given, so a call — whose Transcript is the record —
 * draws nothing here.
 */
function FormRows({ record }: { record: JobInquiry }) {
  const rows = [...(record.facts ?? []), ...(record.answers ?? [])];
  return (
    <>
      {rows.map((row, i) => (
        <Fact key={`${i}:${row.label}`} label={row.label}>
          {row.value}
        </Fact>
      ))}
    </>
  );
}

/**
 * What moves an open enquiry on (migration 071).
 *
 * Every call from a number is written onto one open enquiry, for every
 * Company (migration 077), and nothing reaches the rental system until
 * somebody here says so. Two buttons, under the
 * notes they are about: send it to the rental system, named, so the
 * press says where the job is about to appear; or close it as not an
 * enquiry, which sends nothing. Either way the reader is put back on
 * the list, where a sent enquiry stays while it drafts.
 *
 * Nothing is drawn on an enquiry that is not open: once sent it is the
 * job's, and a change is proposed on the job rather than written here.
 */
function OpenEnquiryActions({ job, onSent }: { job: Job; onSent: () => void }) {
  const [confirm, setConfirm] = useState<"send" | "close" | null>(null);
  const action = useAction();
  const inquiryId = job.inquiry?.inquiry_id ?? "";
  if (job.inquiry_status !== "open" || !inquiryId) return null;
  const rms = job.rental_system || "your rental system";
  const run = (act: (id: string) => Promise<unknown>) =>
    action.run(async () => {
      await act(inquiryId);
      setConfirm(null);
      onSent();
    });
  const cancel = () => {
    setConfirm(null);
    action.setError(null);
  };
  return (
    <div className={styles.openActions}>
      <Button variant="primary" onClick={() => setConfirm("send")}>
        Send to {rms}
      </Button>
      <Button variant="secondary" onClick={() => setConfirm("close")}>
        Not an enquiry
      </Button>
      <ConfirmModal
        open={confirm === "send"}
        title={`Send to ${rms}?`}
        message={`A job is drafted in ${rms} from these notes. Anything they say after this is proposed on that job.`}
        error={action.error}
        confirmLabel={`Send to ${rms}`}
        confirmVariant="primary"
        loading={action.working}
        onConfirm={() => void run(sendInquiry)}
        onCancel={cancel}
      />
      <ConfirmModal
        open={confirm === "close"}
        title="Close as not an enquiry?"
        message={`Nothing is sent to ${rms}. The calls stay on the list.`}
        error={action.error}
        confirmLabel="Close"
        confirmVariant="danger"
        loading={action.working}
        onConfirm={() => void run(closeInquiry)}
        onCancel={cancel}
      />
    </div>
  );
}

/**
 * What became of the one arrival on the page, read off the job it is
 * about and the arrival itself: the Inquiry's status and its failure
 * are the job's, and whether this arrival is the one that drafted it is
 * the entry's.
 */
function outcomeFacts(job: Job, entry: JobEntry): OutcomeFacts {
  return {
    kind: entry.kind,
    inquiry_status: entry.is_inquiry ? job.inquiry_status : "",
    failure_reason: job.failure_reason,
    became_inquiry: entry.is_inquiry,
    rental_system: job.rental_system,
    get_back_to: entry.is_inquiry ? job.inquiry?.get_back_to : "",
    status: entry.status,
    arrived: entry.arrived,
    discard_reason: entry.discard_reason,
    left_message: entry.left_message,
    message_for: entry.message_for,
  };
}

/**
 * What became of a job with a thread under it: the Inquiry that drafted
 * it, read as the arrival it came in as.
 */
function jobOutcomeFacts(
  job: Job,
  inquiry: JobInquiry,
): OutcomeFacts {
  return {
    kind: inquiry.kind,
    inquiry_status: job.inquiry_status,
    failure_reason: job.failure_reason,
    became_inquiry: true,
    rental_system: job.rental_system,
    get_back_to: inquiry.get_back_to,
  };
}

/**
 * One call, opened: its Transcript line with the delete on it, the
 * recording pinned above the words, and the words — the same parts in
 * the same order whether the call is read alone or opened on a rail.
 *
 * The player and the Transcript are kept in step here (#118), and only
 * here: the position is shared between them, and only where there is a
 * recording to line the words up with. Each opening starts its own, so
 * a call closed and opened again starts at the top with nothing lit.
 *
 * Alone, the heading is the page's and the pinned player sits on the
 * page's own background; on the rail it is a sub-heading inside the
 * opened call's box, on the box's. Named, not folded: the words are
 * what a call is opened for.
 */
function CallBody({
  entry,
  onDelete,
  where,
}: {
  entry: JobEntry;
  onDelete: EntryDeleted;
  where: "page" | "box";
}) {
  const playback = usePlayback();
  const Heading = where === "page" ? "h2" : "h3";
  return (
    <>
      <div className={styles.transcriptHead}>
        <Heading
          className={where === "page" ? styles.sectionHeading : styles.subHeading}
        >
          Transcript
        </Heading>
        <DeleteCall entry={entry} onDelete={onDelete} />
      </div>
      {entry.has_recording && (
        // Pinned to the top, because the recording is the source of
        // everything under it.
        <div
          className={
            where === "page"
              ? styles.pinnedPlayerOnPage
              : styles.pinnedPlayerInBox
          }
        >
          <Recording id={entry.id} playback={playback} />
        </div>
      )}
      {entry.transcript ? (
        <Transcript
          text={entry.transcript}
          turns={entry.turns}
          caller={entry.who}
          playback={entry.has_recording ? playback : undefined}
        />
      ) : (
        <p className={styles.nothingSaid}>
          Nothing was transcribed from this call.
        </p>
      )}
    </>
  );
}

/**
 * The one thing done to a call besides reading it: deleting it (#158).
 *
 * On the Transcript's line, because the Transcript is the call itself
 * and what goes is the call: its recording, its words, and the dated
 * line it left under the paragraph. Behind a confirm, as every delete
 * is, and the confirm says what goes. The call the job was drafted from
 * is the thread, so deleting it is deleting the enquiry, and the
 * confirm says that instead. A form submission is deleted from the
 * list, where the job is one row; there is nothing of it to open here.
 *
 * A failure is shown in the confirm, which stays open on it: the press
 * that failed is the one the reader is looking at.
 */
function DeleteCall({
  entry,
  onDelete,
}: {
  entry: JobEntry;
  onDelete: EntryDeleted;
}) {
  const [open, setOpen] = useState(false);
  const action = useAction();
  const whole = entry.is_inquiry;
  const confirm = () =>
    action.run(async () => {
      await onDelete(entry);
      setOpen(false);
    });
  return (
    <>
      <button
        type="button"
        aria-label="Delete call"
        className={styles.deleteCall}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <TrashIcon className={styles.icon} />
      </button>
      <ConfirmModal
        open={open}
        title={whole ? "Delete this enquiry?" : "Delete this call?"}
        message={
          whole
            ? "The enquiry and every call about it are deleted. This cannot be undone."
            : "Its recording and transcript are deleted. This cannot be undone."
        }
        error={action.error}
        confirmLabel="Delete"
        confirmVariant="danger"
        loading={action.working}
        onConfirm={() => void confirm()}
        onCancel={() => {
          setOpen(false);
          action.setError(null);
        }}
      />
    </>
  );
}

/**
 * One entry on the rail, folded.
 *
 * What is on the fold is what a person scanning the thread is scanning
 * for: when it came in, first and in the heading's own weight, because
 * a thread is read by time; how it arrived; and what it said, cut
 * short — the call's summary, or a form submission's own words. Who
 * rang is on the fold only where it was somebody other than the
 * Customer, because every call is theirs unless it says otherwise.
 *
 * Opened, a call is the rest of what one arrival draws: the whole
 * summary in the fold's place, as the one text a call has and the one
 * that is corrected (#119); that other person's email as the one fact
 * that can be typed into; the recording pinned above the Transcript as
 * the source of it; and the Transcript. A form submission's words are
 * the paragraph in the box on top, so on the rail it is its time and
 * nothing to open. A closed entry draws nothing; its recording was read
 * when the thread was, and is held.
 *
 * Every mark on the rail is the same: the entry the job was drafted
 * from is first, which is where a reader looks for it, and a mark
 * picked out in the accent said the same thing a second time.
 *
 * What came of an entry is not on the fold: every follow-up said the
 * same sentence, and the drafting one's said what the heading already
 * says.
 */
function ThreadEntry({
  entry,
  job,
  last,
  onEdited,
  onDelete,
}: {
  entry: JobEntry;
  job: Job;
  last: boolean;
  onEdited: (next: Partial<JobEntry>) => void;
  onDelete: EntryDeleted;
}) {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((was) => !was);
  // The summary is edited in the fold's place, so a press into it is a
  // press to edit and not to close.
  const keep = (e: MouseEvent) => e.stopPropagation();

  const isCall = entry.kind === "call";
  const named = entry.who.trim() !== "";
  const customerPhone = job.inquiry?.contact_phone ?? "";
  // Somebody else rang about the job: a person who gave a name that is
  // not the Customer's, or a number that is not theirs with no name at
  // all. A caller who gave no name from the Customer's own number is
  // the Customer.
  const somebodyElse =
    isCall &&
    !entry.is_inquiry &&
    (named
      ? entry.who !== job.who
      : entry.from_e164 !== "" && entry.from_e164 !== customerPhone);
  const bodyId = `entry-${entry.id}`;
  const thing = isCall ? "call" : "submission";
  // What it said: the call's summary, or the form's own words. A call
  // nobody summarised is folded to who rang, which is the honest label
  // for it. A form submission's words are the paragraph in the box on
  // top, and are not said again on the rail under it.
  const said = isCall
    ? entry.summary || whoLabel(entry.who, entry.from_e164)
    : job.inquiry?.asked_for
      ? ""
      : entry.said;
  // A form submission with nothing to open has no chevron.
  const opens = isCall || said !== "";

  return (
    <li className={styles.entry}>
      {/* The mark (`.mark`) and the line that joins it to the next
          (`.railLine`): the rail is what makes the thread read as a line
          of events before a word of it is read. */}
      <span aria-hidden="true" className={styles.mark} />
      {!last && <span aria-hidden="true" className={styles.railLine} />}
      <div
        className={`${styles.fold} ${opens ? styles.opensOnPress : ""}`}
        onClick={opens ? toggle : undefined}
      >
        <div className={styles.foldBody}>
          <p className={styles.foldHead}>
            <span className={styles.when}>{formatWhenFull(entry.at)}</span>
            <span className={arrivedBadgeClass(entry.arrived)}>
              {arrivedLabel(entry.arrived)}
            </span>
          </p>
          {/* Folded, what was said cut short; opened, a call's summary
              whole and to be corrected in the same place. One of them
              and never both. */}
          {isCall && open ? (
            <div className={styles.openSummary} onClick={keep}>
              <CallSummary
                id={entry.id}
                summary={entry.summary}
                onEdited={onEdited}
                className=""
              />
            </div>
          ) : (
            said && (
              <p
                className={`${styles.said} ${open ? styles.saidWhole : styles.saidShort}`}
              >
                {said}
              </p>
            )
          )}
          {somebodyElse && (
            <p className={styles.who}>
              {whoLine(entry.who, entry.who_org, entry.from_e164)}
            </p>
          )}
        </div>
        {opens && (
          <button
            type="button"
            aria-expanded={open}
            aria-controls={isCall ? bodyId : undefined}
            aria-label={open ? `Hide ${thing}` : `Show ${thing}`}
            className={styles.chevron}
          >
            <ChevronDownIcon
              className={`${styles.chevronIcon} ${open ? styles.flipped : ""}`}
            />
          </button>
        )}
      </div>

      {open && isCall && (
        <div id={bodyId} className={styles.openCall}>
          {somebodyElse && named && (
            <dl className={styles.callerRows}>
              <CallEmail id={entry.id} email={entry.email} onEdited={onEdited} />
            </dl>
          )}
          <CallBody entry={entry} onDelete={onDelete} where="box" />
        </div>
      )}
    </li>
  );
}
